import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { PushSpinner } from "react-spinners-kit";
import './Spinner.scss';

/**
 * Spinner component for displaying loading states
 * Optimized with React.memo and useMemo for better performance
 */
const Spinner = React.memo(({ 
    size = 30, 
    color = "#1B62C0", 
    loading = true, 
    fullscreen = true,
    className = "",
    ariaLabel = "Loading content",
    testId = "spinner-component"
}) => {
    // Memoize container styles to prevent unnecessary recalculations
    const containerStyles = useMemo(() => ({
        position: fullscreen ? 'fixed' : 'relative',
        display: loading ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        ...(fullscreen && {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 5
        })
    }), [fullscreen, loading]);

    // Memoize spinner props to prevent unnecessary re-renders
    const spinnerProps = useMemo(() => ({
        size,
        color,
        loading
    }), [size, color, loading]);

    // Don't render anything if loading is false
    if (!loading) {
        return null;
    }

    return (
        <div 
            className={`${fullscreen ? 'fullscreen' : ''} ${className}`.trim()}
            style={containerStyles}
            role="status"
            aria-live="polite"
            aria-label={ariaLabel}
            data-testid={testId}
        >
            <PushSpinner {...spinnerProps} />
        </div>
    );
});

// Display name for debugging
Spinner.displayName = 'Spinner';

// PropTypes for type safety and documentation
Spinner.propTypes = {
    /** Size of the spinner in pixels */
    size: PropTypes.number,
    /** Color of the spinner */
    color: PropTypes.string,
    /** Whether the spinner should be shown */
    loading: PropTypes.bool,
    /** Whether the spinner should cover the full screen */
    fullscreen: PropTypes.bool,
    /** Additional CSS class names */
    className: PropTypes.string,
    /** Accessibility label for screen readers */
    ariaLabel: PropTypes.string,
    /** Test ID for testing purposes */
    testId: PropTypes.string
};

export default Spinner;